import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, X, GripVertical, Lock, Users, FileText, Search, Rocket, Calendar, TrendingUp, UserCheck, LayoutTemplate, Copy, CheckSquare } from 'lucide-react';
import { templatesApi, teamsApi } from '@/utils/api';

const ICON_MAP = { FileText, Users, Search, Rocket, Calendar, TrendingUp, UserCheck, LayoutTemplate };
const ICON_OPTIONS = Object.keys(ICON_MAP);
const TIcon = ({ name, size = 15 }) => { const C = ICON_MAP[name] || FileText; return <C size={size} />; };

const emptyTemplate = () => ({ name: '', icon: 'FileText', meeting_context: '', sections: [{ title: '', description: '' }], preset_tasks: [], visibility: 'private', team_id: null });

export default function TemplatesManager({ open, onOpenChange }) {
  const [templates, setTemplates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // template being viewed/edited
  const [draft, setDraft] = useState(null);          // editable copy
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, tm] = await Promise.all([templatesApi.list(), teamsApi.list().catch(() => ({ data: {} }))]);
      setTemplates(t.data || []);
      setTeams(tm.data.teams || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { load(); setSelected(null); setDraft(null); } }, [open]);

  const isBuiltin = selected?.is_builtin;
  const startNew = () => { const t = emptyTemplate(); setSelected({ _new: true }); setDraft(t); };
  const openTemplate = (t) => { setSelected(t); setDraft(t.is_builtin ? null : JSON.parse(JSON.stringify({ ...t, preset_tasks: t.preset_tasks || [] }))); };
  const duplicate = (t) => {
    const src = t || {};
    setSelected({ _new: true });
    setDraft({
      name: `${(src.name || 'Untitled').trim()} (copy)`,
      icon: src.icon || 'FileText',
      meeting_context: src.meeting_context || '',
      sections: (src.sections && src.sections.length)
        ? src.sections.map(s => ({ title: s.title || '', description: s.description || '' }))
        : [{ title: '', description: '' }],
      preset_tasks: (src.preset_tasks || []).map(p => ({ title: p.title || '', due_offset_days: p.due_offset_days ?? '', priority: p.priority || 'medium' })),
      visibility: 'private',
      team_id: null,
    });
    toast.message('Editing a copy — save to create your own template');
  };

  const updateSection = (i, key, val) => setDraft(d => ({ ...d, sections: d.sections.map((s, idx) => idx === i ? { ...s, [key]: val } : s) }));
  const addSection = () => setDraft(d => ({ ...d, sections: [...d.sections, { title: '', description: '' }] }));
  const removeSection = (i) => setDraft(d => ({ ...d, sections: d.sections.filter((_, idx) => idx !== i) }));

  const updatePreset = (i, key, val) => setDraft(d => ({ ...d, preset_tasks: d.preset_tasks.map((p, idx) => idx === i ? { ...p, [key]: val } : p) }));
  const addPreset = () => setDraft(d => ({ ...d, preset_tasks: [...(d.preset_tasks || []), { title: '', due_offset_days: '', priority: 'medium' }] }));
  const removePreset = (i) => setDraft(d => ({ ...d, preset_tasks: d.preset_tasks.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Give your template a name'); return; }
    const cleanSections = draft.sections.filter(s => s.title.trim());
    if (cleanSections.length === 0) { toast.error('Add at least one section'); return; }
    if (draft.visibility === 'team' && !draft.team_id) { toast.error('Choose a workspace to share with'); return; }
    const cleanPresets = (draft.preset_tasks || []).filter(p => (p.title || '').trim()).map(p => ({
      title: p.title.trim(),
      priority: p.priority || 'medium',
      due_offset_days: p.due_offset_days === '' || p.due_offset_days == null ? null : parseInt(p.due_offset_days, 10),
    }));
    setSaving(true);
    try {
      const payload = { ...draft, sections: cleanSections, preset_tasks: cleanPresets };
      if (selected?._new) {
        const res = await templatesApi.create(payload);
        toast.success('Template created');
        await load(); openTemplate(res.data);
      } else {
        const res = await templatesApi.update(selected.id, payload);
        toast.success('Template saved');
        await load(); openTemplate(res.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await templatesApi.remove(selected.id);
      toast.success('Template deleted');
      setSelected(null); setDraft(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  const mine = templates.filter(t => !t.is_builtin && t.visibility !== 'team');
  const shared = templates.filter(t => !t.is_builtin && t.visibility === 'team');
  const builtin = templates.filter(t => t.is_builtin);

  const Group = ({ label, items }) => items.length > 0 && (
    <div className="mb-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">{label}</p>
      {items.map(t => (
        <button
          key={t.id}
          data-testid={`template-item-${t.id}`}
          onClick={() => openTemplate(t)}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${selected?.id === t.id ? 'bg-[#6994c6]/10' : 'hover:bg-secondary/50'}`}
        >
          <span className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(105,148,198,0.12)', color: '#6994c6' }}><TIcon name={t.icon} /></span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-foreground truncate">{t.name}</span>
            <span className="block text-[11px] text-muted-foreground">{(t.sections || []).length} sections</span>
          </span>
          {t.is_builtin ? <Lock size={12} className="text-muted-foreground" /> : t.visibility === 'team' ? <Users size={12} className="text-muted-foreground" /> : null}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden gap-0" style={{ height: '80vh' }} data-testid="templates-manager">
        <div className="flex h-full">
          {/* Left rail */}
          <div className="w-64 border-r border-border flex flex-col" style={{ background: '#FBFAF6' }}>
            <div className="p-3 border-b border-border">
              <h2 className="text-sm font-bold flex items-center gap-2 mb-2"><LayoutTemplate size={15} style={{ color: '#6994c6' }} />Templates</h2>
              <button
                data-testid="template-new-button"
                onClick={startNew}
                className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium h-9 rounded-lg text-white transition-colors"
                style={{ background: '#6994c6' }}
              >
                <Plus size={14} />New template
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-muted-foreground" size={20} /></div> : (
                <>
                  <Group label="My templates" items={mine} />
                  <Group label="Shared with workspace" items={shared} />
                  <Group label="Built-in" items={builtin} />
                </>
              )}
            </div>
          </div>

          {/* Right pane */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {!selected && (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <LayoutTemplate size={40} className="mb-3 opacity-40" />
                <p className="text-sm max-w-xs">Select a template to view it, or create your own to control how Milk structures your notes.</p>
              </div>
            )}

            {selected && isBuiltin && (
              <div data-testid="template-view-builtin">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(105,148,198,0.12)', color: '#6994c6' }}><TIcon name={selected.icon} size={18} /></span>
                  <h3 className="text-lg font-bold">{selected.name}</h3>
                  <span className="text-[11px] px-2 py-0.5 rounded-md bg-secondary text-muted-foreground flex items-center gap-1"><Lock size={10} />Built-in · view only</span>
                </div>
                {selected.meeting_context && <p className="text-sm text-muted-foreground mt-3 mb-4">{selected.meeting_context}</p>}
                <div className="space-y-2">
                  {selected.sections.map((s, i) => (
                    <div key={i} className="rounded-xl border border-border p-3">
                      <p className="text-sm font-semibold">{s.title}</p>
                      {s.description && <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected && draft && (
              <div data-testid="template-editor" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">{selected._new ? 'New template' : 'Edit template'}</h3>
                  {!selected._new && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => duplicate(draft)} data-testid="template-duplicate-button" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6994c6] hover:bg-[#6994c6]/5 px-2.5 py-1.5 rounded-lg transition-colors">
                        <Copy size={13} />Duplicate
                      </button>
                      <button onClick={remove} data-testid="template-delete-button" className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors">
                        <Trash2 size={13} />Delete
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Select value={draft.icon} onValueChange={(v) => setDraft(d => ({ ...d, icon: v }))}>
                    <SelectTrigger className="w-16 h-11" data-testid="template-icon-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(ic => <SelectItem key={ic} value={ic}><span className="flex items-center gap-2"><TIcon name={ic} />{ic}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input
                    data-testid="template-name-input"
                    value={draft.name}
                    onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                    placeholder="Template name (e.g. Client Review)"
                    className="flex-1 h-11 px-3.5 rounded-xl text-sm outline-none border"
                    style={{ background: '#FBFAF6', borderColor: 'rgba(33,46,64,.14)' }}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground">Meeting context</label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Tell Milk the purpose of this type of conversation — it shapes how notes are written.</p>
                  <textarea
                    data-testid="template-context-input"
                    value={draft.meeting_context}
                    onChange={(e) => setDraft(d => ({ ...d, meeting_context: e.target.value }))}
                    rows={2}
                    placeholder="e.g. A quarterly client portfolio review. Capture holdings discussed, decisions and next steps."
                    className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none border resize-none"
                    style={{ background: '#FBFAF6', borderColor: 'rgba(33,46,64,.14)' }}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground">Sections</label>
                  <p className="text-[11px] text-muted-foreground mb-2">The headings your notes will be organised into.</p>
                  <div className="space-y-2">
                    {draft.sections.map((s, i) => (
                      <div key={i} className="rounded-xl border border-border p-2.5 flex gap-2" data-testid={`template-section-${i}`}>
                        <GripVertical size={14} className="text-muted-foreground mt-2 shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <input
                            value={s.title}
                            onChange={(e) => updateSection(i, 'title', e.target.value)}
                            placeholder="Section title (e.g. Decisions)"
                            data-testid={`template-section-title-${i}`}
                            className="w-full h-9 px-3 rounded-lg text-sm font-medium outline-none border"
                            style={{ background: '#fff', borderColor: 'rgba(33,46,64,.12)' }}
                          />
                          <input
                            value={s.description}
                            onChange={(e) => updateSection(i, 'description', e.target.value)}
                            placeholder="What to capture in this section (optional)"
                            className="w-full h-8 px-3 rounded-lg text-xs outline-none border"
                            style={{ background: '#fff', borderColor: 'rgba(33,46,64,.12)' }}
                          />
                        </div>
                        {draft.sections.length > 1 && (
                          <button onClick={() => removeSection(i)} className="text-muted-foreground hover:text-red-500 p-1 h-fit" data-testid={`template-section-remove-${i}`}><X size={14} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={addSection} data-testid="template-add-section" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#6994c6] hover:bg-[#6994c6]/5 px-2.5 py-1.5 rounded-lg transition-colors">
                    <Plus size={13} />Add section
                  </button>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground">Pre-set tasks</label>
                  <p className="text-[11px] text-muted-foreground mb-2">Created automatically every time this template is used — e.g. recurring follow-ups for a director's meeting. Due dates are set relative to the capture date.</p>
                  <div className="space-y-2">
                    {(draft.preset_tasks || []).map((p, i) => (
                      <div key={i} className="rounded-xl border border-border p-2.5 flex items-center gap-2" data-testid={`template-preset-${i}`}>
                        <CheckSquare size={14} className="text-muted-foreground shrink-0" />
                        <input
                          value={p.title}
                          onChange={(e) => updatePreset(i, 'title', e.target.value)}
                          placeholder="Task title (e.g. Circulate minutes to the board)"
                          data-testid={`template-preset-title-${i}`}
                          className="flex-1 h-9 px-3 rounded-lg text-sm outline-none border min-w-0"
                          style={{ background: '#fff', borderColor: 'rgba(33,46,64,.12)' }}
                        />
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[11px] text-muted-foreground">due +</span>
                          <input
                            type="number" min="0" max="365"
                            value={p.due_offset_days ?? ''}
                            onChange={(e) => updatePreset(i, 'due_offset_days', e.target.value)}
                            placeholder="—"
                            data-testid={`template-preset-due-${i}`}
                            className="w-14 h-9 px-2 rounded-lg text-sm outline-none border text-center"
                            style={{ background: '#fff', borderColor: 'rgba(33,46,64,.12)' }}
                          />
                          <span className="text-[11px] text-muted-foreground">days</span>
                        </div>
                        <Select value={p.priority || 'medium'} onValueChange={(v) => updatePreset(i, 'priority', v)}>
                          <SelectTrigger className="w-24 h-9 shrink-0" data-testid={`template-preset-priority-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                        <button onClick={() => removePreset(i)} className="text-muted-foreground hover:text-red-500 p-1 shrink-0" data-testid={`template-preset-remove-${i}`}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <button onClick={addPreset} data-testid="template-add-preset" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#6994c6] hover:bg-[#6994c6]/5 px-2.5 py-1.5 rounded-lg transition-colors">
                    <Plus size={13} />Add pre-set task
                  </button>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground">Sharing</label>
                  <div className="flex gap-2 mt-1.5">
                    <Select
                      value={draft.visibility}
                      onValueChange={(v) => setDraft(d => ({ ...d, visibility: v, team_id: v === 'private' ? null : d.team_id }))}
                    >
                      <SelectTrigger className="w-44 h-10" data-testid="template-visibility-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private"><span className="flex items-center gap-2"><Lock size={13} />Private (only me)</span></SelectItem>
                        <SelectItem value="team"><span className="flex items-center gap-2"><Users size={13} />Share with workspace</span></SelectItem>
                      </SelectContent>
                    </Select>
                    {draft.visibility === 'team' && (
                      <Select value={draft.team_id || ''} onValueChange={(v) => setDraft(d => ({ ...d, team_id: v }))}>
                        <SelectTrigger className="flex-1 h-10" data-testid="template-team-select"><SelectValue placeholder="Choose a workspace…" /></SelectTrigger>
                        <SelectContent>
                          {teams.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">You're not in any workspace yet.</div>}
                          {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>

            {/* Sticky footer — always reachable regardless of content length */}
            {selected && isBuiltin && (
              <div className="shrink-0 flex justify-end gap-2 p-4 border-t border-border bg-white">
                <button onClick={() => duplicate(selected)} data-testid="template-duplicate-button" className="inline-flex items-center gap-2 text-sm font-semibold h-10 px-5 rounded-xl text-white transition-colors" style={{ background: '#6994c6' }}>
                  <Copy size={14} />Duplicate to edit
                </button>
              </div>
            )}
            {selected && draft && (
              <div className="shrink-0 flex justify-end gap-2 p-4 border-t border-border bg-white">
                <button onClick={() => { setSelected(null); setDraft(null); }} className="text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="template-save-button"
                  className="inline-flex items-center gap-2 text-sm font-semibold h-10 px-5 rounded-xl text-white transition-colors disabled:opacity-50"
                  style={{ background: '#6994c6' }}
                >
                  {saving ? <><Loader2 size={14} className="animate-spin" />Saving…</> : 'Save template'}
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
